import { IconButton, Typography } from '@mui/material';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useNewProjectService,
  useProjectSecurityContext,
  useProjectDataContext,
} from 'domain/shared/newProject';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useState } from 'react';
import { Button, Tooltip } from 'framework/ui/mui-builder';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { Modal } from 'framework/ui/ui-builder';

type ArchiveProjectButtonProps = {
  id: string;
  isArchived?: boolean;
};

const ArchiveProjectButton = ({ id, isArchived }: ArchiveProjectButtonProps) => {
  const { isAccessGranted } = useProjectSecurityContext();
  const { refetch } = useProjectDataContext();
  const { archiveProject, unarchiveProject } = useNewProjectService();
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { addMessage } = useMessageService();

  const handleArchiveToggle = async () => {
    setIsLoading(true);
    try {
      if (isArchived) {
        await unarchiveProject(id);
      } else {
        await archiveProject(id);
      }
      await refetch();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to archive/unarchive project'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) ? (
        <Tooltip title={isArchived ? 'Unarchive Project' : 'Archive Project'}>
          <IconButton onClick={() => setModalOpen(true)} disabled={isLoading} color={'primary'}>
            {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
          </IconButton>
        </Tooltip>
      ) : null}
      <Modal
        isOpen={modalOpen}
        title="Are you sure?"
        buttons={
          <Button
            key="modal-confirm-button"
            onClick={async () => {
              await handleArchiveToggle();
              setModalOpen(false);
            }}
            disabled={isLoading}
          >
            Confirm
          </Button>
        }
        onToggle={() => {
          if (!isLoading) {
            setModalOpen(false);
          }
        }}
      >
        <Typography>Do you really want to {isArchived ? 'unarchive' : 'archive'} this project?</Typography>
      </Modal>
    </>
  );
};
export default ArchiveProjectButton;
